// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Composition root for the administration API clients.
 *
 * The provider is the only place an application binds a concrete client to the
 * component tree. Production wires {@link createApiClients}; tests wire a fake
 * that satisfies the same ports, so production code never selects a test
 * double (`TST-042`).
 */

import { createContext, useContext, type ReactNode } from 'react';

import type { ApiClients } from './ports';

const ApiClientContext = createContext<ApiClients | null>(null);

/** Props for {@link ApiClientProvider}. */
export interface ApiClientProviderProps {
  /** The client set to expose to descendants. */
  readonly clients: ApiClients;
  readonly children: ReactNode;
}

/** Provide the administration API clients to a component subtree. */
export function ApiClientProvider({ clients, children }: ApiClientProviderProps): ReactNode {
  return <ApiClientContext.Provider value={clients}>{children}</ApiClientContext.Provider>;
}

/**
 * Read the injected API clients.
 *
 * @returns The client set provided by the nearest {@link ApiClientProvider}.
 * @throws Error If no provider is mounted, which is a wiring defect rather
 *   than a runtime condition a user can reach.
 */
export function useApiClients(): ApiClients {
  const clients = useContext(ApiClientContext);
  if (clients === null) {
    throw new Error('useApiClients requires an <ApiClientProvider> ancestor.');
  }
  return clients;
}
